// Developer: Ankit Gupta
/** Typed client for the Organizations API: the company the person belongs to. */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import i18n from '@/i18n';

export interface Organization {
  id: number;
  slug: string;
  name: string;
  kind: 'COMPANY' | 'PERSONAL';
  status: 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
  is_home: boolean;
}

export interface Invite {
  id: number;
  kind: 'INVITE' | 'REQUEST';
  email: string;
  role: 'ADMIN' | 'MEMBER';
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'REVOKED' | 'EXPIRED';
  expires_at: string | null;
  message: string | null;
  created_at: string;
  created_by: string;
  decided_by: string | null;
  decided_at: string | null;
}

export type PlanStanding = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'NONE' | 'EXPIRED';

export interface PlanSummary {
  code: string | null;
  name: string | null;
  status: PlanStanding;
  days_left: number | null;
  usable: boolean;
  products: Product[];
}

export type Product = 'ORION' | 'MIGRATE' | 'CONFY';

export interface Me {
  status: 'member' | 'none';
  email: string;
  organization: Organization | null;
  role: 'ADMIN' | 'MEMBER' | null;
  is_org_admin: boolean;
  is_platform_admin: boolean;
  domains: { domain: string; verified: boolean; require_sso: boolean }[];
  domain_owner: { name: string } | null;
  pending_request: Invite | null;
  pending_invites: string[];
  domain_is_public: boolean;
  plan: PlanSummary | null;
  sign_in_providers: ('google' | 'microsoft')[];
  purge_after: string | null;
  terms: TermsState | null;
}

export interface TermsState {
  current_version: string;
  accepted_version: string | null;
  accepted: boolean;
  company_version: string | null;
  company_accepted_by: string | null;
  must_accept: boolean;
}

export interface Domain {
  id: number;
  domain: string;
  verified: boolean;
  verified_at: string | null;
  require_sso: boolean;
  record_host: string | null;
  record_value: string | null;
}

export interface PlanChoice {
  code: string;
  name: string;
  description: string | null;
  monthly_price_usd: number;
  monthly_credits: number;
  max_projects: number | null;
  max_members: number | null;
  trial_days: number;
  products: Product[];
}

export interface PlanState {
  plan: PlanChoice | null;
  status: PlanStanding;
  trial_ends_at: string | null;
  days_left: number | null;
  usable: boolean;
  usage: { projects: number; members: number; pending_invites: number };
  requested: PlanChoice | null;
  choices: PlanChoice[];
  payment_mode: 'card' | 'invoice';
  can_manage: boolean;
  paid_by_card: boolean;
  products: Product[];
}

export interface OrionWorker {
  id: number;
  organization_id: number;
  organization_slug: string;
  organization_name: string;
  name: string;
  token_prefix: string;
  status: 'ACTIVE' | 'REVOKED';
  created_by: string;
  created_at: string;
  last_seen_at: string | null;
  online: boolean;
  worker_id: string | null;
  version: string | null;
  channels: string[] | null;
}

export interface OrionWorkerIssued {
  worker: OrionWorker;
  token: string;
  run_command: string;
}

export interface ChooseOut {
  outcome: 'active' | 'checkout' | 'requested';
  checkout_url: string | null;
  state: PlanState;
}

export interface Member {
  id: number;
  user_email: string;
  display_name: string | null;
  role: 'ADMIN' | 'MEMBER';
  joined_at: string | null;
  joined_via: string | null;
}

export interface InviteCreated {
  invite: Invite;
  link: string;
  mailed: boolean;
}

export interface InvitePreview {
  organization: { name: string };
  email: string;
  role: string;
  status: string;
  for_me: boolean;
  expired: boolean;
}

export interface DirectoryRow extends Organization {
  members: number;
  admins: number;
  domains: string[];
  created_at: string;
  plan_code: string | null;
  plan_status: PlanStanding;
  trial_ends_at: string | null;
  requested_plan_code: string | null;
  closed_at: string | null;
  purge_after: string | null;
  paid_by_card: boolean;
  products: Product[];
  products_own: boolean;
}

const BASE = '/org';

export const orgApi = {
  me: () => api.get<Me>(`${BASE}/me`),
  register: (name: string, terms_version: string) => api.post<Me>(`${BASE}/register`, { name, accept_terms: true, terms_version }),
  requestToJoin: (terms_version: string, message?: string) => api.post<Me>(`${BASE}/join-request`, { message, accept_terms: true, terms_version }),
  acceptTerms: (terms_version: string) => api.post<Me>(`${BASE}/terms/accept`, { terms_version }),
  rename: (name: string) => api.patch<Organization>(BASE, { name }),

  members: () => api.get<Member[]>(`${BASE}/members`),
  setRole: (id: number, role: Member['role']) => api.patch<Member>(`${BASE}/members/${id}`, { role }),
  removeMember: (id: number) => api.delete<void>(`${BASE}/members/${id}`),

  invites: () => api.get<Invite[]>(`${BASE}/invites`),
  invite: (email: string, role: Invite['role']) => api.post<InviteCreated>(`${BASE}/invites`, { email, role }),
  revokeInvite: (id: number) => api.delete<Invite>(`${BASE}/invites/${id}`),
  previewInvite: (token: string) => api.get<InvitePreview>(`${BASE}/invites/preview`, { token }),
  acceptInvite: (token: string, terms_version: string) => api.post<Me>(`${BASE}/invites/accept`, { token, accept_terms: true, terms_version }),

  joinRequests: () => api.get<Invite[]>(`${BASE}/join-requests`),
  approve: (id: number) => api.post<Member>(`${BASE}/join-requests/${id}/approve`),
  decline: (id: number) => api.post<Invite>(`${BASE}/join-requests/${id}/decline`),

  directory: () => api.get<DirectoryRow[]>(`${BASE}/directory`),
  setStatus: (id: number, status: 'ACTIVE' | 'SUSPENDED') =>
    api.post<Organization>(`${BASE}/directory/${id}/status`, { status }),
  staffSetPlan: (id: number, body: { plan_code: string | null; status?: 'ACTIVE' | 'TRIAL' | 'NONE'; trial_days?: number; products?: Product[] | null; products_set?: boolean }) =>
    api.post<PlanState>(`${BASE}/directory/${id}/plan`, body),
  purge: (id: number, force = false) => api.delete<{ slug: string; deleted: Record<string, number> }>(`${BASE}/directory/${id}${force ? '?force=true' : ''}`),

  domains: () => api.get<Domain[]>(`${BASE}/domains`),
  addDomain: (domain: string) => api.post<Domain>(`${BASE}/domains`, { domain }),
  verifyDomain: (id: number) => api.post<Domain>(`${BASE}/domains/${id}/verify`),
  setDomainRule: (id: number, require_sso: boolean) => api.patch<Domain>(`${BASE}/domains/${id}`, { require_sso }),
  removeDomain: (id: number) => api.delete<void>(`${BASE}/domains/${id}`),

  plan: () => api.get<PlanState>(`${BASE}/plan`),
  choosePlan: (plan_code: string) => api.post<ChooseOut>(`${BASE}/plan/choose`, { plan_code }),
  cancelPlan: () => api.post<PlanState>(`${BASE}/plan/cancel`),

  orionWorkers: () => api.get<OrionWorker[]>('/orion/workers'),
  issueOrionWorker: (organization_id: number, name = 'orion') =>
    api.post<OrionWorkerIssued>('/orion/workers', { organization_id, name }),
  revokeOrionWorker: (id: number) => api.delete<OrionWorker>(`/orion/workers/${id}`),

  exportUrl: () => `${BASE}/export`,
  close: (confirm: string) => api.post<Me>(`${BASE}/close`, { confirm }),
  reopen: () => api.post<Me>(`${BASE}/reopen`),
};

/** What the server said, for the message a person reads; a 402 carries a plan code. */
export function apiError(e: unknown): { code: string | null; message: string; status: number | null } {
  const resp = (e as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const detail = resp?.data?.detail;
  if (typeof detail === 'string') return { code: null, message: detail, status: resp?.status ?? null };
  if (detail && typeof detail === 'object') {
    const d = detail as { code?: string; message?: string };
    return { code: d.code ?? null, message: d.message ?? i18n.t('company:error'), status: resp?.status ?? null };
  }
  return { code: null, message: i18n.t('company:error'), status: resp?.status ?? null };
}

export const ME_KEY = ['org', 'me'];

/** The signed-in person's organization. Shared by the gate, the menu and the Company page. */
export function useOrgMe() {
  return useQuery({ queryKey: ME_KEY, queryFn: orgApi.me, staleTime: 60_000, retry: 1 });
}
